import { LogoutOutlined, ReloadOutlined } from '@ant-design/icons';
import { type IAdminSessionEntry } from '@mahawi/eshop-common';
import { stringCompare } from '@mahawi/eshop-common/src/string-compare';
import { Button, Popconfirm, Space, Tag, Tooltip, Typography } from 'antd';
import { type ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';

import { EDateFormat } from '../fragments/date-format';
import { EMPTY_VALUE } from '../fragments/empty-value';
import { getTagByRelativeDate } from '../fragments/get-tag-by-relative-date';
import { useAppDispatch } from '../hooks/use-app-dispatch';
import { type RootState } from '../reducers';
import {
  sessionRevoke,
  sessionsLoad,
  sessionsRevokeOthers,
} from '../reducers/user/reducer';
import { SPACING } from '../theme/tokens';
import TableComponent from './table';

const { Title, Text } = Typography;

type TSessionRow = IAdminSessionEntry & { key: string };

const renderOptional = (value: string | null): React.ReactNode =>
  value ?? <Text type="secondary">{EMPTY_VALUE}</Text>;

function AdminSessionsTable(): React.ReactElement {
  const dispatch = useAppDispatch();
  const sessions = useSelector((s: RootState) => s.User.sessions);
  const sessionsInProcess = useSelector(
    (s: RootState) => s.User.sessionsInProcess,
  );

  useEffect(() => {
    dispatch(sessionsLoad());
  }, [dispatch]);

  const columns: ColumnsType<TSessionRow> = [
    {
      title: 'Account',
      key: 'account',
      sorter: (a: TSessionRow, b: TSessionRow): number =>
        stringCompare(a.account.email, b.account.email),
      render: (_: unknown, session: TSessionRow): React.ReactNode => (
        <Space size={SPACING.sm}>
          {session.account.email}
          {session.isCurrent && <Tag color="green">This device</Tag>}
        </Space>
      ),
    },
    {
      title: 'IP',
      dataIndex: 'ip',
      key: 'ip',
      render: renderOptional,
    },
    {
      title: 'Country',
      dataIndex: 'country',
      key: 'country',
      width: 100,
      render: renderOptional,
    },
    {
      title: 'Browser',
      dataIndex: 'userAgent',
      key: 'userAgent',
      ellipsis: { showTitle: false },
      render: (userAgent: string | null): React.ReactNode =>
        userAgent ? (
          <Tooltip title={userAgent}>{userAgent}</Tooltip>
        ) : (
          <Text type="secondary">{EMPTY_VALUE}</Text>
        ),
    },
    {
      title: 'Signed in',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 150,
      sorter: (a: TSessionRow, b: TSessionRow): number =>
        dayjs(a.createdAt).unix() - dayjs(b.createdAt).unix(),
      render: (createdAt: string): string =>
        dayjs(createdAt).format(EDateFormat.DATE_TIME_SHORT),
    },
    {
      title: 'Last activity',
      dataIndex: 'lastSeenAt',
      key: 'lastSeenAt',
      width: 140,
      defaultSortOrder: 'descend',
      sorter: (a: TSessionRow, b: TSessionRow): number =>
        dayjs(a.lastSeenAt ?? a.createdAt).unix() -
        dayjs(b.lastSeenAt ?? b.createdAt).unix(),
      render: (lastSeenAt: string | null): React.ReactNode =>
        lastSeenAt ? (
          <Tooltip
            title={dayjs(lastSeenAt).format(EDateFormat.DATE_TIME_SHORT)}
          >
            {getTagByRelativeDate({ date: new Date(lastSeenAt) })}
          </Tooltip>
        ) : (
          <Text type="secondary">{EMPTY_VALUE}</Text>
        ),
    },
    {
      title: 'Expires',
      dataIndex: 'expiresAt',
      key: 'expiresAt',
      width: 150,
      render: (expiresAt: string): string =>
        dayjs(expiresAt).format(EDateFormat.DATE_TIME_SHORT),
    },
    {
      title: '',
      key: 'actions',
      width: 110,
      align: 'right',
      render: (_: unknown, session: TSessionRow): React.ReactNode =>
        session.isCurrent ? null : (
          <Popconfirm
            title={`Sign out ${session.account.email} on this session?`}
            okText="Revoke"
            okButtonProps={{ danger: true }}
            onConfirm={(): void => {
              dispatch(sessionRevoke(session.uuid));
            }}
          >
            <Button size="small" danger icon={<LogoutOutlined />}>
              Revoke
            </Button>
          </Popconfirm>
        ),
    },
  ];

  return (
    <>
      <Space size={SPACING.lg} align="start">
        <Title level={4}>Active sessions</Title>
        <Tooltip title="Reload">
          <Button
            onClick={(): void => {
              dispatch(sessionsLoad());
            }}
          >
            <ReloadOutlined />
          </Button>
        </Tooltip>
        <Popconfirm
          title="Sign out all your other sessions?"
          okText="Revoke"
          okButtonProps={{ danger: true }}
          onConfirm={(): void => {
            dispatch(sessionsRevokeOthers());
          }}
        >
          <Button danger disabled={sessionsInProcess}>
            Sign out my other sessions
          </Button>
        </Popconfirm>
      </Space>

      <TableComponent
        columns={columns as Parameters<typeof TableComponent>[0]['columns']}
        dataSource={sessions.map(
          (session: IAdminSessionEntry): TSessionRow => ({
            ...session,
            key: session.uuid,
          }),
        )}
        isLoading={sessionsInProcess}
      />
    </>
  );
}

export default AdminSessionsTable;
